import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { crmCampaignsAPI } from '../../services/api';
import {
  DeleteDialog, EditableTable, FieldLabel, SingleGamesSection,
  FREESPIN_COUNT_HINT, apiError, isFreespinCount, isPositiveInt, isValidCurrency, useEditableList,
} from './campaignShared';

// Rank percentages are numeric(10, 5) from 0 to 100; the backend enforces the same.
const isValidPercentage = (s) => /^\d{1,3}(\.\d{1,5})?$/.test(s.trim()) && Number(s) <= 100;

// A limit's range is its identity, but unlike the currency-keyed tables it can be edited: the
// row is addressed by its range as loaded, and the backend refuses any range overlapping another.
function LimitFormDialog({ open, initial, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [freespins, setFreespins] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFrom(initial?.rank_percentage_from ?? '');
    setTo(initial?.rank_percentage_to ?? '');
    setFreespins(initial ? String(initial.freespin_number) : '');
    setError('');
  }, [open, initial]);

  const rangeValid = isValidPercentage(from) && isValidPercentage(to);
  const fromNotBelowTo = rangeValid && Number(from) >= Number(to);
  const freespinsBad = freespins.trim() !== '' && !isFreespinCount(freespins);
  const valid = rangeValid && !fromNotBelowTo && isFreespinCount(freespins);

  const submit = async () => {
    setSaving(true);
    setError('');
    const body = { rank_percentage_from: from.trim(), rank_percentage_to: to.trim(), freespin_number: Number(freespins) };
    try {
      if (editing) {
        await crmCampaignsAPI.updateVipReleaseLimit(initial.rank_percentage_from, initial.rank_percentage_to, body);
      } else {
        await crmCampaignsAPI.createVipReleaseLimit(body);
      }
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save limit.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit rank limit' : 'Add rank limit'}</DialogTitle>
          <DialogDescription>Ranges can't overlap. A range includes its end value but not its start value, so 5–0 and 10–5 can both exist. A range nearer the top (0%) must give more freespins than the ranges after it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            <label className="space-y-1.5 block">
              <FieldLabel>Rank % start</FieldLabel>
              <Input inputMode="decimal" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label className="space-y-1.5 block">
              <FieldLabel>Rank % end</FieldLabel>
              <Input inputMode="decimal" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="space-y-1.5 block">
              <FieldLabel>Freespin count</FieldLabel>
              <Input inputMode="numeric" value={freespins} onChange={(e) => setFreespins(e.target.value)} />
            </label>
          </div>
          {fromNotBelowTo && <p className="text-xs font-medium text-negative">End must be below start.</p>}
          {freespinsBad && <p className="text-xs font-medium text-negative">{FREESPIN_COUNT_HINT}</p>}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add limit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// When editing, the currency is the row's identity and stays read-only — renaming one means
// deleting it and adding it again.
function BetLevelFormDialog({ open, initial, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [currency, setCurrency] = useState('');
  const [betLevel, setBetLevel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCurrency(initial?.currency || '');
    setBetLevel(initial ? String(initial.bet_level) : '');
    setError('');
  }, [open, initial]);

  const valid = isValidCurrency(currency) && isPositiveInt(betLevel);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await crmCampaignsAPI.updateVipReleaseBetLevel(initial.currency, { bet_level: Number(betLevel) });
      } else {
        await crmCampaignsAPI.createVipReleaseBetLevel({ currency: currency.trim(), bet_level: Number(betLevel) });
      }
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save bet level.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit bet level' : 'Add bet level'}</DialogTitle>
          <DialogDescription>A currency can have one bet level.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1.5 block">
              <FieldLabel>Currency</FieldLabel>
              <Input value={currency} disabled={editing} placeholder="e.g. EUR" onChange={(e) => setCurrency(e.target.value)} />
            </label>
            <label className="space-y-1.5 block">
              <FieldLabel>Bet level</FieldLabel>
              <Input inputMode="numeric" value={betLevel} onChange={(e) => setBetLevel(e.target.value)} />
            </label>
          </div>
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add bet level'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Shown as start (rank_percentage_to) then end (rank_percentage_from): counted from the top rank down.
const LIMIT_COLUMNS = [
  { label: 'Rank % start', className: 'text-right', render: (r) => r.rank_percentage_to },
  { label: 'Rank % end', className: 'text-right', render: (r) => r.rank_percentage_from },
  { label: 'Freespin count', className: 'text-right', render: (r) => r.freespin_number },
];

const BET_LEVEL_COLUMNS = [
  { label: 'Currency', className: 'font-bold', render: (r) => r.currency },
  { label: 'Bet level', className: 'text-right', render: (r) => r.bet_level },
];

function LimitsSection() {
  const list = useEditableList(crmCampaignsAPI.listVipReleaseLimits, 'limits');
  const d = list.deleting;
  return (
    <>
      <EditableTable
        defaultCollapsed title="Rank limits" addLabel="Add limit" emptyText="No rank limits yet."
        description="All players eligible for the freespins are ranked by turnover, with 0% at the top. Each row sets how many freespins a percentile range of that ranking receives, e.g. start 5 and end 0 is the top 5%. Better ranks must get more freespins."
        loading={list.loading} error={list.error} rows={list.rows} columns={LIMIT_COLUMNS}
        rowKey={(r) => `${r.rank_percentage_from}-${r.rank_percentage_to}`}
        onAdd={list.openAdd} onEdit={list.openEdit} onDelete={list.setDeleting}
      />
      <LimitFormDialog
        open={list.form.open} initial={list.form.row} onClose={list.closeForm}
        onSaved={() => { list.closeForm(); list.load(); }}
      />
      <DeleteDialog
        open={Boolean(d)} title="Delete rank limit?"
        description={d ? `The limit for rank ${d.rank_percentage_to}–${d.rank_percentage_from}%.` : ''}
        onConfirm={() => crmCampaignsAPI.deleteVipReleaseLimit(d.rank_percentage_from, d.rank_percentage_to)}
        onClose={() => list.setDeleting(null)}
        onDeleted={() => { list.setDeleting(null); list.load(); }}
      />
    </>
  );
}

function BetLevelsSection() {
  const list = useEditableList(crmCampaignsAPI.listVipReleaseBetLevels, 'bet_levels');
  const d = list.deleting;
  return (
    <>
      <EditableTable
        title="Bet levels" addLabel="Add bet level" emptyText="No bet levels yet."
        loading={list.loading} error={list.error} rows={list.rows} columns={BET_LEVEL_COLUMNS}
        rowKey={(r) => r.currency}
        onAdd={list.openAdd} onEdit={list.openEdit} onDelete={list.setDeleting}
      />
      <BetLevelFormDialog
        open={list.form.open} initial={list.form.row} onClose={list.closeForm}
        onSaved={() => { list.closeForm(); list.load(); }}
      />
      <DeleteDialog
        open={Boolean(d)} title="Delete bet level?"
        description={d ? `The ${d.currency} bet level.` : ''}
        onConfirm={() => crmCampaignsAPI.deleteVipReleaseBetLevel(d.currency)}
        onClose={() => list.setDeleting(null)}
        onDeleted={() => { list.setDeleting(null); list.load(); }}
      />
    </>
  );
}

const VipReleasePanel = () => (
  <div className="space-y-6">
    <SingleGamesSection
      load={crmCampaignsAPI.getVipReleaseGames} save={crmCampaignsAPI.setVipReleaseGames} />
    <BetLevelsSection />
    <LimitsSection />
  </div>
);

export default VipReleasePanel;
